import {redirect} from '@sveltejs/kit';
import {get} from 'svelte/store';

import {app_url} from '$lib/svelte_1848_settings.js';
import {post_call, get_requested_state, process_dossier, check_logged_in} from '$lib/utils.js';
import {previous_query, previous_date_range, total, meetings, meetings_total, do_load} from '$lib/store.js';

export async function load({url, fetch, data, depends}) {
    // This allows us to manually rerun this load function by running
    // invalidate('app:agenda:page')
    depends('app:agenda:page');

    let logged_in = check_logged_in(data);

    // If the user is not logged in and requests a dossier, then redirect to the
    // login page
    if (!logged_in && url.searchParams.get('dossier')) {
        throw redirect(302, `${app_url}login`);
    }

    // Get the requested state (some of these values are also available as
    // stores, but we return all values here as normal variables for
    // readability)
    let [query, dossier_id, date_range, date_range_agenda, filters] = get_requested_state(url, data);

    // By default we want to run the requests (do_load == true), but not always
    // (e.g., when navigating back from a document to the feed)
    if (get(do_load)) {
        // Store the initial agenda request
        let request = {
            endpoint: '/search/agenda/',
            body: {
                query: query,
                size: 200,
                dateRange: date_range_agenda,
                sort: "publication_date",
                order: "asc"
            }
        };

        // We only need the document count for $total
        let request_doc_count = {
            endpoint: '/search/',
            body: {
                query: query,
                filters: filters,
                dateRange: date_range,
                search_type: 'count'
            }
        };

        // If the user is logged in and a dossier is selected, update the
        // requests with the correct endpoints and body; remove the query as
        // the database already knows it;
        // If the 'all' dossier is selected
        if (logged_in) {
            if (dossier_id === 'all') {
                request.endpoint = '/search/agenda/dossiers/';
                request.body.query = null;

                request_doc_count.endpoint = '/search/dossiers/';
                request_doc_count.body.query = null;
            // Otherwise a normal dossier is selected
            } else if (dossier_id) {
                request.endpoint = '/search/agenda/dossier/';
                request.body.query = null;
                request.body.dossierID = dossier_id;

                request_doc_count.endpoint = '/search/dossier/';
                request_doc_count.body.query = null;
                request_doc_count.body.dossierID = dossier_id;
            }
        }

        // Get the search request response and save its data in stores
        let response = await post_call(request, fetch, data);
        meetings_total.set(response?.result?.total);
        meetings.set(response?.result?.hits);

        // Don't run when the user navigated here from the app feed as the count
        // will be the same
        // Get the document count request response and save its data in stores;
        // Only run this if:
        //  - $total is null (i.e. when the feed is first loaded)
        //  - or if the query or date filter has changed
        // It thus doesn't run when navigating from the document feed to this
        // feed or when a category is clicked, because the count will be the
        // same in both cases
        if (get(total) === null || get(previous_query) !== query || JSON.stringify(get(previous_date_range)) !== JSON.stringify(date_range)) {
            let response_doc_count = await post_call(request_doc_count, fetch, data);
            total.set(response_doc_count.result.total);
        }

        // If a dossier is selected this will get and set the categories
        // associated with that dossier
        process_dossier();

        // Save the date range and query for later comparison
        previous_date_range.set({...date_range});
        previous_query.set(query);
    } else {
        // If do_load was 'false', set it to the default value of 'true' again
        do_load.set(true);
    }
}
